import { useMemo } from 'react'
import { CustomTrace, LoadingState, RechartData, RechartDataObject } from 'src/Libs/chart-kit'
import { CostSavingChartData } from '../types'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { TraceKey } from 'src/Libs/system-charts/shared'
import { useTheme } from '@mui/material'
import { CostSavingChartTraceKeys } from '../cost-saving-chart-trace-keys'

interface ReturnType {
  chartData: RechartData
  customTraces: CustomTrace[]
  labelTraces: CostSavingChartTraceKeys[]
  legendsNames: Record<string, string>
}
/**
 * This hook prepares the data structure needed for the pie chart component
 * to display home consumption and electric vehicle savings
 */
export const useSavingsPieChartData = ({ data, loadingState }: { data: CostSavingChartData[]; loadingState: LoadingState }): ReturnType => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const chartColor = theme.palette.savingsPieChart

  const transformData = (data: CostSavingChartData[]): RechartData => {
    const ThreeSixtyBatterySavingsSum = data.map(item => item.threeSixtyBatterySavings).reduce((acc, curr) => (acc ?? 0) + (curr ?? 0), 0)
    const ThreeSixtyWallboxSavingsSum = data.map(item => item.threeSixtyWallboxSavings).reduce((acc, curr) => (acc ?? 0) + (curr ?? 0), 0)

    const dataForPieChart = data.map(item => {
      return {
        threeSixtyBatterySavings: item.threeSixtyBatterySavings,
        threeSixtyWallboxSavings: item.threeSixtyWallboxSavings,
      }
    })

    const rechartData = {
      loadingState,
      data: dataForPieChart as RechartDataObject[],
      sums: {
        threeSixtyBatterySavings: { history: ThreeSixtyBatterySavingsSum },
        threeSixtyWallboxSavings: { history: ThreeSixtyWallboxSavingsSum },
      },
    }
    return rechartData
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rechartData = useMemo(() => transformData(data), [data, loadingState])

  const customTraces: CustomTrace[] = [
    {
      key: CostSavingChartTraceKeys.ThreeSixtyBatterySavings as unknown as TraceKey,
      color: chartColor.threeSixtySavingsBatteryColor,
      name: t('dynamic-power-prices.cost-saving-chart.savings-pie-chart.legend-three-sixty-battery-savings'),
    },
    {
      key: CostSavingChartTraceKeys.ThreeSixtyWallboxSavings as unknown as TraceKey,
      color: chartColor.threeSixtySavingsWallboxColor,
      name: t('dynamic-power-prices.cost-saving-chart.savings-pie-chart.legend-three-sixty-wallbox-savings'),
    },
  ]

  const legendsNames = {
    threeSixtyBatterySavings: t('dynamic-power-prices.cost-saving-chart.savings-pie-chart.legend-three-sixty-battery-savings'),
    threeSixtyWallboxSavings: t('dynamic-power-prices.cost-saving-chart.savings-pie-chart.legend-three-sixty-wallbox-savings'),
  }

  const labelTraces = [CostSavingChartTraceKeys.ThreeSixtyBatterySavings, CostSavingChartTraceKeys.ThreeSixtyWallboxSavings]

  return { chartData: rechartData, customTraces, labelTraces, legendsNames }
}
